import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Store, ArrowLeft } from "lucide-react";

const isEmail = (v) => /@/.test(v);

export default function BusinessLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [phase, setPhase] = useState("identify"); // identify | admin | business | forgot
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  // forgot-password flow
  const [fpCode, setFpCode] = useState("");
  const [fpNew, setFpNew] = useState("");
  const [fpNew2, setFpNew2] = useState("");
  const [fpSent, setFpSent] = useState(false);
  const [fpEmail, setFpEmail] = useState("");

  const routeAfter = (role) => navigate(role === "admin" ? "/admin" : "/gate");
  const pwValid = (p) => p.length >= 8 && /[A-Z]/.test(p) && /[a-z]/.test(p) && /\d/.test(p);

  const RememberMe = (
    <label className="flex cursor-pointer items-start gap-2 rounded-lg bg-[#F7F4EE] px-3 py-2.5" data-testid="remember-me-row">
      <Checkbox checked={remember} onCheckedChange={(v) => setRemember(v === true)} className="mt-0.5 data-[state=checked]:bg-[#0D5C3A] data-[state=checked]:border-[#0D5C3A]" data-testid="remember-me-checkbox" />
      <span className="text-sm leading-tight text-stone-600">
        <span className="font-medium text-stone-800">Remember me</span>
        <span className="block text-xs text-stone-500">Stay signed in on this device, even after closing the app.</span>
      </span>
    </label>
  );

  const onContinue = async () => {
    const id = identifier.trim();
    if (!id) return;
    if (isEmail(id)) { setPhase("admin"); return; }
    if (!id.startsWith("+")) { toast.error("Enter a mobile number in international format, e.g. +9198..., or your admin email"); return; }
    setPhase("business");
  };

  const forceLogin = async (kind) => {
    setBusy(true);
    try {
      const url = kind === "admin" ? "/auth/admin/login-password" : "/auth/business/login-password";
      const payload = kind === "admin"
        ? { email: identifier.trim(), password, remember, force: true }
        : { mobile: identifier.trim(), password, remember, force: true };
      const res = await api.post(url, payload);
      login(res.data.token, res.data.user, remember); toast.success("Signed in"); routeAfter(kind);
    } catch (e) { toast.error(e.response?.data?.detail || "Login failed"); } finally { setBusy(false); }
  };

  const businessPassword = async () => {
    setBusy(true);
    try {
      const res = await api.post("/auth/business/login-password", { mobile: identifier.trim(), password, remember });
      login(res.data.token, res.data.user, remember); toast.success("Signed in"); routeAfter("business");
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error("Your account is already signed in on another device.", {
          duration: 12000,
          action: { label: "Sign out other device", onClick: () => forceLogin("business") },
        });
      } else { toast.error(e.response?.data?.detail || "Login failed"); }
    } finally { setBusy(false); }
  };

  const adminPassword = async () => {
    setBusy(true);
    try {
      const res = await api.post("/auth/admin/login-password", { email: identifier.trim(), password, remember });
      login(res.data.token, res.data.user, remember); toast.success("Signed in"); routeAfter("admin");
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error("This admin account is active on another device.", {
          duration: 12000,
          action: { label: "Sign Out Admin From Other Devices", onClick: () => forceLogin("admin") },
        });
      } else { toast.error(e.response?.data?.detail || "Login failed"); }
    } finally { setBusy(false); }
  };

  const adminSendOtp = async () => {
    setBusy(true);
    try {
      const res = await api.post("/auth/admin/send-otp", { email: identifier.trim() });
      setOtpSent(true);
      if (res.data.dev_otp) toast.info(`Dev OTP: ${res.data.dev_otp}`, { duration: 8000 });
      else toast.success("Code sent to your email");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const forceVerifyOtp = async () => {
    setBusy(true);
    try {
      const res = await api.post("/auth/admin/verify-otp", { email: identifier.trim(), code: otp, remember, force: true });
      login(res.data.token, res.data.user, remember); toast.success("Signed in"); routeAfter("admin");
    } catch (e) { toast.error(e.response?.data?.detail || "Login failed"); } finally { setBusy(false); }
  };

  const adminVerifyOtp = async () => {
    setBusy(true);
    try {
      const res = await api.post("/auth/admin/verify-otp", { email: identifier.trim(), code: otp, remember });
      login(res.data.token, res.data.user, remember); toast.success("Signed in"); routeAfter("admin");
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error("This admin account is active on another device.", {
          duration: 15000,
          action: { label: "Sign Out From Other Devices", onClick: forceVerifyOtp },
        });
      } else { toast.error(e.response?.data?.detail || "Invalid code"); }
    } finally { setBusy(false); }
  };

  // ---- forgot password ----
  const startForgot = () => { setPhase("forgot"); setFpSent(false); setFpCode(""); setFpNew(""); setFpNew2(""); setFpEmail(""); };
  const sendResetOtp = async () => {
    if (!/@/.test(fpEmail.trim())) { toast.error("Enter your registered business email"); return; }
    setBusy(true);
    try {
      await api.post("/auth/business/forgot-password-email", { email: fpEmail.trim(), mobile: identifier.trim() });
      setFpSent(true);
      toast.success("If this email is registered, a reset code has been sent.");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const doReset = async () => {
    if (!pwValid(fpNew)) { toast.error("Password needs 8+ chars with uppercase, lowercase and a number"); return; }
    if (fpNew !== fpNew2) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      const res = await api.post("/auth/business/reset-password-email", { email: fpEmail.trim(), code: fpCode, new_password: fpNew, remember, mobile: identifier.trim() });
      login(res.data.token, res.data.user, remember); toast.success("Password updated — signed in"); routeAfter("business");
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); } finally { setBusy(false); }
  };

  const reset = () => { setPhase("identify"); setOtp(""); setPassword(""); setOtpSent(false); };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF8F5] rv-grain p-6">
      <div className="w-full max-w-md rounded-3xl border border-[#E5E0D8] bg-white p-8 shadow-sm">
        <Link to="/" className="mb-6 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-[#0D5C3A]"><ArrowLeft className="h-4 w-4" /> Home</Link>
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0D5C3A] text-white"><Store className="h-6 w-6" /></div>
          <div>
            <h1 className="font-heading text-xl font-bold text-stone-900">Sign In</h1>
            <p className="text-sm text-stone-500">Enter your mobile number or email</p>
          </div>
        </div>

        {phase === "identify" && (
          <div className="space-y-4">
            <div><Label>Mobile number or email</Label>
              <Input data-testid="login-identifier-input" className="mt-1" placeholder="+911111100001" value={identifier} onChange={(e) => setIdentifier(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onContinue()} /></div>
            <Button data-testid="login-continue-btn" disabled={busy} onClick={onContinue} className="w-full bg-[#0D5C3A] hover:bg-[#084229]">Continue</Button>
          </div>
        )}

        {phase === "admin" && (
          <div className="space-y-4">
            <div className="rounded-lg bg-[#F0ECE1] px-3 py-2 text-sm text-stone-600">{identifier}</div>
            {!otpSent ? (
              <>
                <div><Label>Password</Label>
                  <Input data-testid="login-password-input" type="password" className="mt-1" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && adminPassword()} /></div>
                {RememberMe}
                <Button data-testid="login-password-btn" disabled={busy} onClick={adminPassword} className="w-full bg-[#0D5C3A] hover:bg-[#084229]">Sign In</Button>
                <button className="w-full text-sm text-[#0D5C3A] hover:underline" data-testid="login-use-email-otp" onClick={adminSendOtp}>Use email code instead</button>
              </>
            ) : (
              <>
                <div><Label>Enter code</Label>
                  <Input data-testid="login-otp-input" className="mt-1 tracking-widest" placeholder="6-digit code" value={otp} onChange={(e) => setOtp(e.target.value)} onKeyDown={(e) => e.key === "Enter" && adminVerifyOtp()} /></div>
                {RememberMe}
                <Button data-testid="login-verify-btn" disabled={busy} onClick={adminVerifyOtp} className="w-full bg-[#0D5C3A] hover:bg-[#084229]">Verify & Sign In</Button>
              </>
            )}
            <button className="w-full text-xs text-stone-500 hover:underline" onClick={reset}>Use a different account</button>
          </div>
        )}

        {phase === "business" && (
          <div className="space-y-4">
            <div className="rounded-lg bg-[#F0ECE1] px-3 py-2 text-sm text-stone-600">{identifier}</div>
            <div><Label>Password</Label>
              <Input data-testid="login-bpassword-input" type="password" className="mt-1" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && businessPassword()} /></div>
            {RememberMe}
            <Button data-testid="login-bpassword-btn" disabled={busy} onClick={businessPassword} className="w-full bg-[#0D5C3A] hover:bg-[#084229]">Sign In</Button>
            <button className="w-full text-sm text-[#0D5C3A] hover:underline" data-testid="login-forgot-link" onClick={startForgot}>Forgot password?</button>
            <button className="w-full text-xs text-stone-500 hover:underline" onClick={reset}>Change number</button>
          </div>
        )}

        {phase === "forgot" && (
          <div className="space-y-4" data-testid="forgot-flow">
            <div className="rounded-lg bg-[#F0ECE1] px-3 py-2 text-sm text-stone-600">Reset your password</div>
            {!fpSent ? (
              <>
                <p className="text-sm text-stone-500">Enter your business's registered email and we'll send a one-time reset code. Operators: use your business owner's registered email.</p>
                <div><Label>Registered email</Label>
                  <Input data-testid="forgot-email-input" type="email" className="mt-1" placeholder="you@business.com" value={fpEmail} onChange={(e) => setFpEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendResetOtp()} /></div>
                <Button data-testid="forgot-send-btn" disabled={busy} onClick={sendResetOtp} className="w-full bg-[#0D5C3A] hover:bg-[#084229]">Send reset code</Button>
              </>
            ) : (
              <>
                <div><Label>Reset code</Label>
                  <Input data-testid="forgot-code-input" className="mt-1 tracking-widest" placeholder="6-digit code" value={fpCode} onChange={(e) => setFpCode(e.target.value)} /></div>
                <div><Label>New password</Label>
                  <Input data-testid="forgot-newpw-input" type="password" className="mt-1" value={fpNew} onChange={(e) => setFpNew(e.target.value)} /></div>
                <div><Label>Confirm new password</Label>
                  <Input data-testid="forgot-newpw2-input" type="password" className="mt-1" value={fpNew2} onChange={(e) => setFpNew2(e.target.value)} /></div>
                <p className="text-xs text-stone-500">Min 8 chars with uppercase, lowercase and a number.</p>
                <Button data-testid="forgot-reset-btn" disabled={busy} onClick={doReset} className="w-full bg-[#0D5C3A] hover:bg-[#084229]">Update password & sign in</Button>
              </>
            )}
            <button className="w-full text-xs text-stone-500 hover:underline" onClick={() => setPhase("business")}>Back to sign in</button>
          </div>
        )}

        <p className="mt-6 text-center text-sm text-stone-500">
          New here? <Link to="/register" className="font-semibold text-[#0D5C3A] hover:underline" data-testid="login-to-register-link">Register your business</Link>
        </p>
      </div>
    </div>
  );
}
